import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { DashboardLayout } from "src/components/dashboard";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import Select from "react-select";
import { useUpdateDeliveryCredentialMutation, useDeleteDeliveryCredentialMutation } from "src/store/services/partnerApi";

import {
  useLazyGetDeliveryPartnerQuery,
  useGetPartnerCategoryByIdQuery,
} from "src/store/services/partnerApi";

interface CredentialField {
  label: string;
  key: string;
  value: string;
  is_secret: boolean;
}

interface DeliveryCredential {
  _id: string;
  artisan_id: string;
  partner_name: string;
  credential_fields: CredentialField[];
  is_active: boolean;
  environment: string;
  deleted: boolean;
  createdAt: string;
  updatedAt: string;
}

interface FormData {
  environment: string;
  credential_fields: CredentialField[];
  outlets: string[];
}

const MIN_CREDENTIAL_FIELDS = 2;

const EditDeliveryPartner = () => {
  const { partnerId = "" } = useParams<{ partnerId: string }>();

  const navigate = useNavigate();

  const [getCredentials, { data, isLoading, isError }] =
    useLazyGetDeliveryPartnerQuery();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePartner, { isLoading: isDeleting }] =
  useDeleteDeliveryCredentialMutation();

  const handleOpenDeleteModal = () => {
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
  };

  useEffect(() => {
    getCredentials({
      id: partnerId,
    });
  }, [getCredentials]);

  const credentials: DeliveryCredential[] = data?.payload?.credentials || [];

  console.log(credentials);
  const [error, setError] = useState<string | null>(null);

  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [selectedCategoryOption, setSelectedCategoryOption] =
    useState<any>(null);

  // ======================
  // FETCH CATEGORIES
  // ======================
  const [updatePartner, { isLoading: isUpdating }] =
    useUpdateDeliveryCredentialMutation();

  // ======================
  // FETCH PARTNERS
  // ======================
  const { data: partnersData, isLoading: partnersLoading } =
    useGetPartnerCategoryByIdQuery(
      { page: 1, id: selectedCategory, limit: 1000 },
      { skip: !selectedCategory },
    );

  // ======================
  // OUTLET OPTIONS
  // ======================
  const partnerOptions =
    partnersData?.payload?.partners?.map((p: any) => ({
      value: p._id || p.partner_id || p.id,
      label: p.business_name || p.name || "Unnamed Outlet",
    })) || [];

  // ======================
  // FORM STATE
  // ======================
  const [formData, setFormData] = useState<FormData>({
    environment: "test",
    outlets: [],
    credential_fields: [
      {
        label: "Merchant ID",
        key: "merchantId",
        value: "",
        is_secret: false,
      },
      {
        label: "API Key",
        key: "apiKey",
        value: "",
        is_secret: false,
      },
    ],
  });

  useEffect(() => {
    if (credentials.length > 0) {
      const mappedFields = credentials[0].credential_fields.map((field) => ({
        label:
          field.key === "merchantId"
            ? "Merchant ID"
            : field.key === "apiKey"
              ? "API Key"
              : field.key,
        key: field.key,
        value: field.value,
        is_secret: field.is_secret,
      }));

      // Guarantee at least MIN_CREDENTIAL_FIELDS entries even if the API
      // returns fewer than that.
      const paddedFields = [...mappedFields];
      while (paddedFields.length < MIN_CREDENTIAL_FIELDS) {
        paddedFields.push({
          label: "",
          key: "",
          value: "",
          is_secret: false,
        });
      }

      setFormData((prev) => ({
        ...prev,
        environment: credentials[0]?.environment || "test",
        credential_fields: paddedFields,
      }));
    }
  }, [credentials]);

  // ======================
  // ENVIRONMENT
  // ======================
  const handleEnvironmentChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormData((prev) => ({
      ...prev,
      environment: e.target.value,
    }));
  };

  // ======================
  // FIELD CHANGE
  // ======================
  const handleFieldChange = (
    index: number,
    field: keyof CredentialField,
    value: string | boolean,
  ) => {
    const updated = [...formData.credential_fields];

    updated[index] = {
      ...updated[index],
      [field]: value,
    };

    setFormData((prev) => ({
      ...prev,
      credential_fields: updated,
    }));
  };

  // ======================
  // OUTLETS CHANGE
  // ======================
  const handleOutletChange = (option: any) => {
    setFormData((prev) => ({
      ...prev,
      outlets: option ? [option.value] : [],
    }));
  };

  // ======================
  // ADD FIELD
  // ======================
  const addCredentialField = () => {
    setFormData((prev) => ({
      ...prev,
      credential_fields: [
        ...prev.credential_fields,
        {
          label: "",
          key: "",
          value: "",
          is_secret: false,
        },
      ],
    }));
  };

  // ======================
  // REMOVE FIELD
  // ======================
  const removeCredentialField = (index: number) => {
    if (formData.credential_fields.length <= MIN_CREDENTIAL_FIELDS) {
      toast.warning(
        `At least ${MIN_CREDENTIAL_FIELDS} credential fields are required`,
      );
      return;
    }

    setFormData((prev) => ({
      ...prev,
      credential_fields: prev.credential_fields.filter((_, i) => i !== index),
    }));
  };

  // ======================
  // DELETE
  // ======================
  const handleDelete = async () => {
    try {
      await deletePartner(credentials[0]._id).unwrap();

      toast.success("Credentials deleted successfully");

      setShowDeleteModal(false);

      setTimeout(() => {
        navigate(`/partners/details/${partnerId}`);
      }, 800);
    } catch (err: any) {
      const message = err?.data?.message || err?.message || "Failed to delete";

      toast.error(message);
    }
  };

  // ======================
  // SUBMIT
  // ======================
  const handleSubmit = async () => {
    setError(null);

    if (!formData.environment) {
      setError("Environment is required");
      return;
    }

    if (formData.credential_fields.length < MIN_CREDENTIAL_FIELDS) {
      setError(`At least ${MIN_CREDENTIAL_FIELDS} credential fields are required`);
      return;
    }

    for (const field of formData.credential_fields) {
      if (!field.label || !field.key || !field.value) {
        setError("All credential fields are required");
        return;
      }
    }

    try {
      const payload = {
        environment: formData.environment,
        credential_fields: formData.credential_fields,
      };

      console.log("PAYLOAD:", payload);

      const response = await updatePartner({
        partnerId,
        formData: payload, // send JSON, NOT FormData
      }).unwrap();

      console.log("UPDATE RESPONSE:", response);

      toast.success("Credentials updated successfully");

      setTimeout(() => {
        navigate(`/partners/details/${partnerId}`);
      }, 1000);
    } catch (err: any) {
      console.error(err);

      const message = err?.data?.message || err?.message || "Failed to save";

      setError(message);
      toast.error(message);
    }
  };

  const canRemoveFields = formData.credential_fields.length > MIN_CREDENTIAL_FIELDS;

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto p-6 bg-white rounded-xl shadow-sm">
        <h2 className="text-2xl font-semibold mb-2">
          Edit Delivery Partner Credentials
        </h2>

        <p className="text-gray-500 mb-8">Configure provider credentials.</p>

        {showDeleteModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg w-[400px] shadow-lg">
              <h2 className="text-xl font-semibold mb-3">Delete Credentials</h2>

              <p className="text-gray-600 mb-6">
                Are you sure you want to delete these credentials? This action
                cannot be undone.
              </p>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={handleCloseDeleteModal}
                  className="px-4 py-2 border rounded"
                >
                  Cancel
                </button>

                <button
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="px-4 py-2 bg-red-700 text-white rounded"
                >
                  {isDeleting ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* FIELDS */}
        {formData.credential_fields.map((field, index) => (
          <div key={index} className="border p-4 mb-4 rounded relative">
            <input
              placeholder="Label"
              value={field.label}
              onChange={(e) =>
                handleFieldChange(index, "label", e.target.value)
              }
              className="border p-2 w-full mb-2"
            />

            <input
              placeholder="Key"
              value={field.key}
              onChange={(e) => handleFieldChange(index, "key", e.target.value)}
              className="border p-2 w-full mb-2"
            />

            <input
              placeholder="Value"
              type={field.is_secret ? "password" : "text"}
              value={field.value}
              onChange={(e) =>
                handleFieldChange(index, "value", e.target.value)
              }
              className="border p-2 w-full mb-2"
            />

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={field.is_secret}
                  onChange={(e) =>
                    handleFieldChange(index, "is_secret", e.target.checked)
                  }
                />
                Secret
              </label>

              <button
                type="button"
                onClick={() => removeCredentialField(index)}
                disabled={!canRemoveFields}
                title={
                  !canRemoveFields
                    ? `At least ${MIN_CREDENTIAL_FIELDS} fields are required`
                    : "Remove this field"
                }
                className={`text-sm px-3 py-1 rounded border ${
                  !canRemoveFields
                    ? "text-gray-400 border-gray-200 cursor-not-allowed"
                    : "text-red-600 border-red-300 hover:bg-red-50"
                }`}
              >
                Remove
              </button>
            </div>
          </div>
        ))}

        {/* ADD FIELD */}
        <button
          type="button"
          onClick={addCredentialField}
          className="text-purple-600 border border-purple-300 hover:bg-purple-50 px-4 py-2 rounded mb-6"
        >
          + Add Field
        </button>

        {/* ERROR */}
        {error && <div className="text-red-600 mb-4">{error}</div>}

        {/* SUBMIT */}
        <button
          onClick={handleSubmit}
          disabled={isUpdating}
          className="bg-purple-600 text-white w-full p-3 rounded"
        >
          {isUpdating ? "Updating..." : "Update Credentials"}
        </button>
        <button
          onClick={handleOpenDeleteModal}
          className="bg-red-800 text-white w-full p-3 rounded mt-3"
        >
          {isDeleting ? "Deleting..." : "Delete Credentials"}
        </button>

        <ToastContainer />
      </div>
    </DashboardLayout>
  );
};

export default EditDeliveryPartner;